import { z } from "zod";
import { allowedVideoEmbedUrl } from "./video-urls";

const httpsUrl = z
  .string()
  .trim()
  .max(500)
  .url()
  .refine((value) => {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password;
  }, "Use an HTTPS URL without credentials");
const date = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => {
    const parsed = new Date(value);
    return (
      !Number.isNaN(parsed.getTime()) &&
      parsed.toISOString().slice(0, 10) === value
    );
  }, "Use a valid date");
/** Clinical review dates follow the Canberra pilot's calendar, including before UTC midnight. */
export function studioDate(value = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Australia/Sydney",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(value);
}

export const studioPackageSchema = z
  .object({
    schemaVersion: z.literal(1),
    title: z.string().trim().min(1).max(90),
    specialistName: z.string().trim().min(1).max(120),
    summary: z.string().trim().min(1).max(350),
    takeaways: z.array(z.string().trim().min(1).max(160)).min(1).max(3),
    videoUrl: httpsUrl.refine(
      (value) => Boolean(allowedVideoEmbedUrl(value)),
      "Use a supported YouTube or Vimeo address",
    ),
    durationSeconds: z.number().int().min(1).max(900),
    references: z
      .array(
        z
          .object({ title: z.string().trim().min(1).max(100), url: httpsUrl })
          .strict(),
      )
      .min(1)
      .max(5),
    review: z
      .object({
        reviewer: z.string().trim().min(1).max(120),
        reviewedOn: date,
        reviewDue: date,
        disclosures: z.string().trim().min(1).max(250),
        speakerApproved: z.literal(true),
        captionsChecked: z.literal(true),
        patientFree: z.literal(true),
      })
      .strict(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const today = studioDate();
    if (value.review.reviewedOn > today)
      ctx.addIssue({
        code: "custom",
        message: "Review date cannot be in the future",
      });
    if (
      value.review.reviewDue <= today ||
      value.review.reviewDue <= value.review.reviewedOn
    )
      ctx.addIssue({
        code: "custom",
        message:
          "Review due date must follow the review date and be in the future",
      });
  });
export type StudioPackage = z.infer<typeof studioPackageSchema>;
export function studioPackageBody(pkg: StudioPackage): string {
  return [
    pkg.summary,
    pkg.takeaways.map((item) => `- ${item}`).join("\n"),
    `Sources:\n${pkg.references.map((ref) => `- [${ref.title.replace(/[\[\]]/g, "")}](${ref.url.replace(/\(/g, "%28").replace(/\)/g, "%29")})`).join("\n")}`,
    `Reviewed by ${pkg.review.reviewer} · ${pkg.review.reviewedOn}. Review due ${pkg.review.reviewDue}.`,
    `Disclosures: ${pkg.review.disclosures}`,
    `Video: ${Math.floor(pkg.durationSeconds / 60)}:${String(pkg.durationSeconds % 60).padStart(2, "0")}. Speaker-approved; captions checked.`,
  ].join("\n\n");
}
export function parseStudioPackage(input: unknown) {
  const pkg = studioPackageSchema.parse(input);
  const body = studioPackageBody(pkg);
  if (body.length > 1500)
    throw new Error(
      "Session notes exceed 1,500 characters. Shorten the summary, takeaways or reference labels.",
    );
  return { pkg, body };
}
export function recordingBrief(title: string, speaker: string) {
  return `# Five Minute CPD — recording brief\n\nTopic: ${title.trim()}\nSpeaker: ${speaker.trim()}\nAudience: Australian GPs and primary care practitioners\n\n## The question\nWhat is the single practical question this session answers?\n\n## Five-minute structure\n0:00–0:20 — The everyday GP question and why it matters.\n0:20–1:00 — The context and what must be checked first.\n1:00–3:40 — Two or three practical considerations, with important exceptions.\n3:40–4:30 — When to seek specialist advice; limitations and uncertainty.\n4:30–5:00 — Three take-home points and the sources supporting them.\n\n## Record on your iPhone\nLandscape, camera at eye level, quiet room, stable phone, light in front. Use 1080p, 30 fps. Leave a few seconds before and after speaking. If you make a mistake, pause and repeat the sentence. Keep the original recording.\n\n## Send with the recording\nPreferred name and credentials; 2–3 current source links; disclosures (including none, if applicable); permission to edit and proposed publication channels. Use the agreed private transfer method. No patient details, records, screens or identifiable cases.\n\n## Production\nCreate a presenter project in the existing PulseBoard YouTube Studio. Preserve source/take.mov. Transcribe, pre-edit, compile and review before rendering. Use the channel's approved title, captions and motion templates. Speaker and clinical reviewer approve the final edit, captions, sources and disclosures.\n\n## Release\nNo upload, publication or accredited CPD claim is authorised by this brief. Record actual learning time; confirm any CPD provider arrangement separately.\n`;
}
export function downloadText(
  filename: string,
  content: string,
  type = "text/plain",
) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
